import React, { useEffect, useMemo, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import {
  Table,
  Button,
  Spinner,
  Form,
  Modal,
  Row,
  Col,
  Badge,
  Card,
} from "react-bootstrap";

import api from "../components/Api";

const QuestionBankAll = () => {
  const [questions, setQuestions] = useState([]);
  const [filtered, setFiltered] = useState([]);
  const [loading, setLoading] = useState(true);

  const [filterType, setFilterType] = useState("ALL");
  const [sortOrder, setSortOrder] = useState("desc");

  const [currentPage, setCurrentPage] = useState(1);

  const [isDemo, setIsDemo] = useState(false);

  const [search, setSearch] = useState("");
 
  const [uploading, setUploading] = useState(false);

  const navigate = useNavigate();

  const itemsPerPage = 20;

  // ================= MEDIA MODAL =================
  const [showMediaModal, setShowMediaModal] = useState(false);

  const [mediaType, setMediaType] = useState("video");

  const [selectedQuestion, setSelectedQuestion] = useState(null);

  const [mediaForm, setMediaForm] = useState({
    title: "",
    description: "",

    video_type: "url",
    video_url: "",
    video_file: null,

    ppt_type: "url",
    ppt_url: "",
    ppt_file: null,
  });

  // ================= LOAD QUESTIONS =================
  useEffect(() => {
    getquestionList();
  }, [isDemo]);

 const getquestionList = () => {

  setLoading(true);

  api
    .get("/pustak-questionbank/grouped", {
      params: {
        is_demo: isDemo ? 1 : 0,
      },
    })
    .then((res) => {

      if (res.data.status === "success") {

        const list = res.data.data || [];

        setQuestions(list);

        setFiltered(list);

      } else {

        setQuestions([]);

        setFiltered([]);
      }

    })
    .catch((err) => {

      console.error(
        "Error fetching question list:",
        err
      );

      setQuestions([]);

      setFiltered([]);

    })
    .finally(() => {

      setLoading(false);

    });
};

  // ================= FILTER + SEARCH =================
  useEffect(() => {
    let temp = [...questions];

    // TYPE FILTER
    if (filterType !== "ALL") {
      temp = temp.filter((q) => q.questype === filterType);
    }

    // SEARCH
    if (search.trim()) {
      temp = temp.filter((q) => {
        const text = `
          ${q.bank_name || ""}
          ${q.questype || ""}
          ${q.seriesname || ""}
          ${q.bookname || ""}
          ${q.description || ""}
        `.toLowerCase();

        return text.includes(search.toLowerCase());
      });
    }

    // SORT
    temp.sort((a, b) => {
      const dateA = new Date(a.created_at);
      const dateB = new Date(b.created_at);

      return sortOrder === "asc"
        ? dateA - dateB
        : dateB - dateA;
    });

    setFiltered(temp);

    setCurrentPage(1);
  }, [questions, filterType, search, sortOrder]);

  // ================= PAGINATION =================
  const totalPages = Math.ceil(
    filtered.length / itemsPerPage
  );

  const paginated = useMemo(() => {
    return filtered.slice(
      (currentPage - 1) * itemsPerPage,
      currentPage * itemsPerPage
    );
  }, [filtered, currentPage]);

  // ================= DELETE =================
  const handleDelete = async (id) => {
    if (
      !window.confirm(
        `Delete this ${
          isDemo ? "DEMO" : ""
        } question set?`
      )
    )
      return;

    try {
      const url =
  `/pustak-questionbank/delete/${id}?is_demo=${isDemo ? 1 : 0}`;

      const res = await api.delete(url);

      if (res.data.status === "success") {
        alert("Deleted successfully!");

        getquestionList();
      }
    } catch (error) {
      alert("Failed to delete question.");
    }
  };

  // ================= OPEN MEDIA MODAL =================
  const openMediaModal = (question, type) => {
    setSelectedQuestion(question);

    setMediaType(type);

    setMediaForm({
      title: "",
      description: "",

      video_type: "url",
      video_url: "",
      video_file: null,

      ppt_type: "url",
      ppt_url: "",
      ppt_file: null,
    });

    setShowMediaModal(true);
  };

  // ================= HANDLE FORM =================
  const handleMediaChange = (e) => {
    const { name, value } = e.target;

    setMediaForm({
      ...mediaForm,
      [name]: value,
    });
  };

  // ================= HANDLE FILE =================
  const handleFileChange = (e) => {
    const { name, files } = e.target;

    setMediaForm({
      ...mediaForm,
      [name]: files[0],
    });
  };

  // ================= SAVE MEDIA =================
  const _handleMediaUpload = async () => {
    try {
      const formData = new FormData();

      formData.append(
        "question_id",
        selectedQuestion?.id
      );

      formData.append("isDemo", isDemo ? 1 : 0);

      formData.append("title", mediaForm.title);

      formData.append(
        "description",
        mediaForm.description
      );

      // ================= VIDEO =================
      if (mediaType === "video") {
        formData.append(
          "video_type",
          mediaForm.video_type
        );

        if (mediaForm.video_type === "url") {
          formData.append(
            "video_url",
            mediaForm.video_url
          );
        }

        if (
          mediaForm.video_type === "upload" &&
          mediaForm.video_file
        ) {
          formData.append(
            "video_file",
            mediaForm.video_file
          );
        }

        await api.post(
          "/question-media/video/upload",
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );
      }

      // ================= PPT =================
      if (mediaType === "ppt") {
        formData.append(
          "ppt_type",
          mediaForm.ppt_type
        );

        if (mediaForm.ppt_type === "url") {
          formData.append(
            "ppt_url",
            mediaForm.ppt_url
          );
        }

        if (
          mediaForm.ppt_type === "upload" &&
          mediaForm.ppt_file
        ) {
          formData.append(
            "ppt_file",
            mediaForm.ppt_file
          );
        }

        await api.post(
          "/question-media/ppt/upload",
          formData,
          {
            headers: {
              "Content-Type":
                "multipart/form-data",
            },
          }
        );
      }

      alert(
        `${mediaType.toUpperCase()} uploaded successfully`
      );

      setShowMediaModal(false);
    } catch (err) {
      console.log(err);

      alert("Upload failed");
    }
  };

  const handleMediaUpload = async () => {
  try {

    setUploading(true);

    const formData = new FormData();

    formData.append(
      "question_id",
      selectedQuestion?.id
    );

    formData.append("isDemo", isDemo ? 1 : 0);

    formData.append("title", mediaForm.title);

    formData.append(
      "description",
      mediaForm.description
    );

    // ================= VIDEO =================
    if (mediaType === "video") {

      formData.append(
        "video_type",
        mediaForm.video_type
      );

      if (mediaForm.video_type === "url") {

        formData.append(
          "video_url",
          mediaForm.video_url
        );
      }

      if (
        mediaForm.video_type === "upload" &&
        mediaForm.video_file
      ) {

        formData.append(
          "video_file",
          mediaForm.video_file
        );
      }

      await api.post(
        "/question-media/video/upload",
        formData,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );
    }

    // ================= PPT =================
    if (mediaType === "ppt") {

      formData.append(
        "ppt_type",
        mediaForm.ppt_type
      );

      if (mediaForm.ppt_type === "url") {

        formData.append(
          "ppt_url",
          mediaForm.ppt_url
        );
      }

      if (
        mediaForm.ppt_type === "upload" &&
        mediaForm.ppt_file
      ) {

        formData.append(
          "ppt_file",
          mediaForm.ppt_file
        );
      }

      await api.post(
        "/question-media/ppt/upload",
        formData,
        {
          headers: {
            "Content-Type":
              "multipart/form-data",
          },
        }
      );
    }

    alert(
      `${mediaType.toUpperCase()} uploaded successfully`
    );

    setShowMediaModal(false);

  } catch (err) {

    console.log(err);

    alert("Upload failed");

  } finally {

    setUploading(false);
  }
};

  // ================= LOADING =================
  if (loading)
    return (
      <div className="text-center mt-5">
        <Spinner animation="border" />
      </div>
    );

   const cloneToDemo = async (bankId) => {

  if (!bankId) {

    alert("Invalid Bank ID");

    return;
  }

  try {

    const res = await api.post(
      `/pustak-questionbank/clone-to-demo/${bankId}`
    );

    if (res.data.status === "success") {

      alert(
        res.data.message ||
        "Cloned Successfully"
      );

      getquestionList();

    } else {

      alert(
        res.data.message ||
        "Clone failed"
      );
    }

  } catch (err) {

    console.log(err);

    alert("Clone failed");
  }
};

  return (
    <div className="container-fluid py-4">
      {/* ================= HEADER ================= */}
      <div className="d-flex justify-content-between align-items-center flex-wrap gap-3 mb-4">
        <div>
          <h3 className="fw-bold mb-1">
            {isDemo
              ? "🧪 Demo Question Bank"
              : "📘 Question Bank"}
          </h3>

          <p className="text-muted mb-0">
            Manage question banks, videos & PPTs
          </p>
        </div>

        <div className="d-flex align-items-center gap-3">
          <Form.Check
            type="switch"
            id="demo-mode-switch"
            label="Demo Mode"
            checked={isDemo}
            onChange={(e) =>
              setIsDemo(e.target.checked)
            }
            className="fw-bold"
          />

          <Button
            as={Link}
            to="/dashboard/add-question"
            variant="primary"
          >
            ➕ Add Question
          </Button>
        </div>
      </div>

      {/* ================= FILTER CARD ================= */}
      <Card className="shadow-sm border-0 rounded-4 mb-4">
        <Card.Body>
          <Row className="g-3 align-items-center">
            {/* SEARCH */}
            <Col md={5}>
              <Form.Control
                type="text"
                placeholder="🔍 Search question bank..."
                value={search}
                onChange={(e) =>
                  setSearch(e.target.value)
                }
              />
            </Col>

            {/* FILTER */}
            <Col md={3}>
              <Form.Select
                value={filterType}
                onChange={(e) =>
                  setFilterType(e.target.value)
                }
              >
                <option value="ALL">
                  All Types
                </option>

                <option value="MCQ">MCQ</option>

                <option value="NOTES">
                  Notes
                </option>

                <option value="PPT">PPT</option>

                <option value="TRUE/FALSE">
                  True/False
                </option>

                <option value="VIDEO">
                  Video
                </option>
              </Form.Select>
            </Col>

            {/* SORT */}
            <Col md={2}>
              <Button
                variant="outline-secondary"
                className="w-100"
                onClick={() =>
                  setSortOrder(
                    sortOrder === "asc"
                      ? "desc"
                      : "asc"
                  )
                }
              >
                Sort{" "}
                {sortOrder === "asc"
                  ? "↑"
                  : "↓"}
              </Button>
            </Col>

            {/* TOTAL */}
            <Col md={2}>
              <div className="text-end fw-bold">
                Total: {filtered.length}
              </div>
            </Col>
          </Row>
        </Card.Body>
      </Card>

      {/* ================= TABLE ================= */}
      <div className="table-responsive shadow-sm rounded-4 overflow-hidden">
        <Table bordered hover className="mb-0">
          <thead className="table-dark align-middle">
            <tr>
              <th>#</th>

              <th>Question Bank</th>

              <th>Type</th>

              <th>Subject</th>

              <th>Chapter</th>

              <th>Created</th>

              <th width="350">Actions</th>
            </tr>
          </thead>
            <tbody>
            {paginated.map((q, i) => (
              <tr key={i}>
                <td>{i + 1}</td>

                <td>
              <div className="fw-bold text-dark fs-6">
                {q.seriesname} {q.bookname}
              </div>

              <div className="text-primary fw-semibold mt-1">
                {q.bank_name}
              </div>
              </td>
      <td>
        <Badge bg="primary">
          {q.total_questions} Questions
        </Badge>
      </td>

      <td>
        🎥 {q.videos}
      </td>

      <td>
        📊 {q.ppts}
      </td>

      <td>
        {new Date(q.created_at).toLocaleString()}
      </td>

      <td>
        <Button
          variant="info"
          size="sm"
          onClick={() =>
            navigate(
              `/dashboard/question-bank/bank/${q.book_id}?is_demo=${isDemo}`
            )
          }
        >
          👁 Open Bank
        </Button>

      {!isDemo && (
  <Button
    variant="outline-warning"
    size="sm"
    className="ms-2"
    onClick={() => cloneToDemo(q.bank_id)}
  >
    Clone To Demo
  </Button>
)}
      </td>
    </tr>
  ))}
</tbody>
         {/* <tbody>
            {paginated.length > 0 ? (
              paginated.map((q, i) => (
                <tr key={q.id}>
                  <td>
                    {(currentPage - 1) *
                      itemsPerPage +
                      i +
                      1}
                  </td>

                   <td>
                    <div className="fw-semibold">
                      {q.bank_name || "-"}
                    </div>

                    {q.description && (
                      <small className="text-muted">
                        {q.description.substring(
                          0,
                          50
                        )}
                        ...
                      </small>
                    )}
                  </td>

                  <td>
                    <Badge bg="primary">
                      {q.questype}
                    </Badge>
                  </td>

                  <td>
                    {q.seriesname || "-"}
                  </td>

                  <td>{q.bookname || "-"}</td>

                  <td>
                    {new Date(
                      q.created_at
                    ).toLocaleString()}
                  </td>
 <td>
                    <div className="d-flex flex-wrap gap-2">
                     
                      <Button
                        variant="info"
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/dashboard/question-bank/view/${q.id}${
                              isDemo
                                ? "?demo=true"
                                : ""
                            }`
                          )
                        }
                      >
                        👁 View
                      </Button>

                      <Button
                        variant="warning"
                        size="sm"
                        onClick={() =>
                          navigate(
                            `/dashboard/question-bank/edit/${q.id}${
                              isDemo
                                ? "?demo=true"
                                : ""
                            }`
                          )
                        }
                      >
                        ✏ Edit
                      </Button>

                      <Button
                        variant="outline-danger"
                        size="sm"
                        onClick={() =>
                          openMediaModal(
                            q,
                            "video"
                          )
                        }
                      >
                        🎥 Video
                      </Button>

                      <Button
                        variant="outline-primary"
                        size="sm"
                        onClick={() =>
                          openMediaModal(
                            q,
                            "ppt"
                          )
                        }
                      >
                        📊 PPT
                      </Button>

                      <Button
                        variant="danger"
                        size="sm"
                        onClick={() =>
                          handleDelete(q.id)
                        }
                      >
                        🗑 Delete
                      </Button>
                    </div>
                  </td>
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan="7"
                  className="text-center py-4"
                >
                  No records found
                </td>
              </tr>
            )}
          </tbody>*/}
          
        </Table>
      </div>

      {/* ================= PAGINATION ================= */}
      <div className="d-flex justify-content-center align-items-center mt-4 gap-3">
        <Button
          variant="secondary"
          size="sm"
          disabled={currentPage === 1}
          onClick={() =>
            setCurrentPage((p) => p - 1)
          }
        >
          Prev
        </Button>

        <span className="fw-semibold">
          Page {currentPage} of {totalPages}
        </span>

        <Button
          variant="secondary"
          size="sm"
          disabled={
            currentPage === totalPages
          }
          onClick={() =>
            setCurrentPage((p) => p + 1)
          }
        >
          Next
        </Button>
      </div>

      {/* ================= MEDIA MODAL ================= */}
      <Modal
        show={showMediaModal}
        onHide={() =>
          setShowMediaModal(false)
        }
        centered
        size="lg"
      >
        <Modal.Header closeButton>
          <Modal.Title>
            {mediaType === "video"
              ? "🎥 Add Video"
              : "📊 Add PPT"}
          </Modal.Title>
        </Modal.Header>

        <Modal.Body>
          <Form>
            {/* TITLE */}
            <Form.Group className="mb-3">
              <Form.Label>Title</Form.Label>

              <Form.Control
                type="text"
                name="title"
                value={mediaForm.title}
                onChange={handleMediaChange}
              />
            </Form.Group>

            {/* DESCRIPTION */}
            <Form.Group className="mb-3">
              <Form.Label>Description</Form.Label>

              <Form.Control
                as="textarea"
                rows={3}
                name="description"
                value={mediaForm.description}
                onChange={handleMediaChange}
              />
            </Form.Group>

           
            {/* VIDEO */}
{mediaType === "video" && (
  <>
    <Form.Group className="mb-3">
      <Form.Label>Video Type</Form.Label>

      <Form.Select
        name="video_type"
        value={mediaForm.video_type}
        onChange={handleMediaChange}
      >
        <option value="url">
          Youtube URL
        </option>

        <option value="upload">
          Upload Video File
        </option>
      </Form.Select>
    </Form.Group>

    {mediaForm.video_type === "url" ? (
      <Form.Group className="mb-3">
        <Form.Label>Youtube URL</Form.Label>

        <Form.Control
          type="text"
          name="video_url"
          value={mediaForm.video_url}
          onChange={handleMediaChange}
        />
      </Form.Group>
    ) : (
      <Form.Group className="mb-3">
        <Form.Label>Upload Video</Form.Label>

        <Form.Control
          type="file"
          name="video_file"
          accept="video/*"
          onChange={handleFileChange}
        />
      </Form.Group>
    )}
  </>
)}

           {/* PPT */}
{mediaType === "ppt" && (
  <>
    <Form.Group className="mb-3">
      <Form.Label>PPT Type</Form.Label>

      <Form.Select
        name="ppt_type"
        value={mediaForm.ppt_type}
        onChange={handleMediaChange}
      >
        <option value="url">
          PPT URL
        </option>

        <option value="upload">
          Upload PPT File
        </option>
      </Form.Select>
    </Form.Group>

    {mediaForm.ppt_type === "url" ? (
      <Form.Group className="mb-3">
        <Form.Label>PPT URL</Form.Label>

        <Form.Control
          type="text"
          name="ppt_url"
          value={mediaForm.ppt_url}
          onChange={handleMediaChange}
        />
      </Form.Group>
    ) : (
      <Form.Group className="mb-3">
        <Form.Label>Upload PPT</Form.Label>

        <Form.Control
          type="file"
          name="ppt_file"
          accept=".ppt,.pptx,.pdf"
          onChange={handleFileChange}
        />
      </Form.Group>
    )}
  </>
)}
          </Form>
        </Modal.Body>

        <Modal.Footer>
          <Button
            variant="secondary"
            onClick={() =>
              setShowMediaModal(false)
            }
          >
            Cancel
          </Button>

          <Button
  variant="primary"
  onClick={handleMediaUpload}
  disabled={uploading}
>
  {uploading ? (
    <>
      <Spinner
        animation="border"
        size="sm"
        className="me-2"
      />
      Uploading...
    </>
  ) : (
    <>
      Save {mediaType.toUpperCase()}
    </>
  )}
</Button>
        </Modal.Footer>
      </Modal>
    </div>
  );
};

export default QuestionBankAll;